/**
 * Content primitives.
 *
 * Deliberately small and single-purpose. Two design rules taken from Elementor, both of which
 * they arrived at only after rewriting their element model twice:
 *
 * 1. **Semantic tag is decoupled from visual size.** A Heading picks its level (h1–h4, which
 *    is what Google and screen readers read) separately from its scale (which is what it
 *    looks like). Conflating them is how pages end up with five H1s because someone wanted
 *    big text — the live site already does this, using `<h3>` for stat numbers.
 * 2. **One DOM node per element.** No decorative wrappers; if you want a wrapper, add a Stack.
 */

import type { AssetRef } from '@lp/shared';
import {
  el,
  text as textNode,
  type BlockSignal,
  type BlockSpec,
  type CtaValue,
  type IRContext,
  type IRNode,
  type Issue,
  type LinkValue,
} from '@lp/shared';
import { isValidAnalyticsId, type ButtonRadius, type ButtonSize } from '../shared/ctaEmbed.js';
import { buttonNode, buttonStyleDefaults, buttonStyleFields, buttonStyleFrom } from '../shared/button.js';
import { INLINE_HELP, inlineLinks, parseInline, plainInline } from '../shared/inline.js';
import { TYPE_STYLE_DEFAULTS, typeStyleCombos, typeStyleFields, type TypeStyleProps } from '../shared/typeStyle.js';
import { alignCombo, boxCls, isDark, onDarkCombo, textCls, ALIGN_OPTIONS } from '../shared/util.js';
import { ctaGroupCls } from '../shared/patterns.js';

/* ================================================================ *
 * Heading
 * ================================================================ */

const SCALE_TO_CLASS: Record<string, string> = {
  display: 'lp-display',
  h1: 'lp-h1',
  h2: 'lp-h2',
  h3: 'lp-h3',
  h4: 'lp-h4',
  panel: 'lp-h2-panel',
};

export interface HeadingProps extends Record<string, unknown>, TypeStyleProps {
  content: string;
  level: '1' | '2' | '3' | '4';
  scale: string;
  align: 'left' | 'center' | 'right';
  onDark: boolean;
}

export const Heading: BlockSpec<HeadingProps> = {
  id: 'Heading',
  label: 'Heading',
  category: 'both',

  fields: {
    content: { type: 'text', label: 'Text', lint: 'heading' },
    level: {
      type: 'radio',
      label: 'Heading level',
      help: 'What Google and screen readers read. One H1 per page, and never skip a level.',
      options: [1, 2, 3, 4].map((n) => ({ label: `H${n}`, value: String(n) })),
    },
    scale: {
      type: 'select',
      label: 'Size',
      help: 'Purely visual — independent of the heading level.',
      options: [
        { label: 'Display (64px)', value: 'display' },
        { label: 'Extra large (52px)', value: 'h1' },
        { label: 'Large (52px, tighter)', value: 'h2' },
        { label: 'Panel (45px)', value: 'panel' },
        { label: 'Medium (24px)', value: 'h3' },
        { label: 'Small (20px)', value: 'h4' },
      ],
    },
    align: { type: 'radio', label: 'Alignment', options: [...ALIGN_OPTIONS] },
    ...typeStyleFields,
    onDark: { type: 'boolean', label: 'On a dark background' },
  } as BlockSpec<HeadingProps>['fields'],

  defaultProps: {
    content: 'A clear, specific heading',
    level: '2',
    scale: 'h2',
    align: 'left',
    ...TYPE_STYLE_DEFAULTS,
    onDark: false,
  },

  toIR(props): IRNode {
    const base = SCALE_TO_CLASS[props.scale] ?? 'lp-h2';
    return el(
      'Heading',
      `h${props.level}`,
      textCls(
        base,
        alignCombo(base, props.align),
        ...typeStyleCombos(base, props),
        onDarkCombo(base, props.onDark),
      ),
      parseInline(props.content),
    );
  },

  signals(props): BlockSignal[] {
    const plain = plainInline(props.content);
    return [
      { kind: 'heading', level: Number(props.level) as 1 | 2 | 3 | 4, text: plain },
      { kind: 'text', fieldPath: 'content', value: plain, scope: 'heading' },
    ];
  },

  validate(props): Issue[] {
    if (!props.content.trim()) {
      return [
        {
          id: 'heading-empty',
          severity: 'warn',
          message: 'This heading is empty, which leaves a hole in the page outline.',
          location: { fieldPath: 'content' },
        },
      ];
    }
    return [];
  },
};

/* ================================================================ *
 * Text
 * ================================================================ */

/**
 * Text.
 *
 * This block absorbed the Rich text block, which is gone. That block wrapped a second editor
 * with its own value format, and the format was the bug: Puck hands `render()` a React element
 * rather than the HTML string it stores, so the canvas printed `[object Object]` while the
 * export emitted correct markup. It also arrived with padding nobody asked for and no way to
 * remove it.
 *
 * What it could do that a plain field could not was emphasise part of a sentence. That now
 * comes from `parseInline` — `**bold**`, `*italic*`, `[link](url)` in an ordinary textarea,
 * producing native Webflow `Strong` / `Emphasized` / `Link` nodes. One value format, one
 * editor, and the emphasis stays editable in the Designer instead of being frozen in an embed.
 */
export interface TextBlockProps extends Record<string, unknown>, TypeStyleProps {
  content: string;
  scale: string;
  align: 'left' | 'center' | 'right';
  onDark: boolean;
}

export const TextBlock: BlockSpec<TextBlockProps> = {
  id: 'TextBlock',
  label: 'Text',
  category: 'both',
  description: 'A paragraph. Supports bold, italic and links inline.',

  fields: {
    content: { type: 'textarea', label: 'Text', help: INLINE_HELP, lint: 'body' },
    scale: {
      type: 'select',
      label: 'Style',
      options: [
        { label: 'Lead (24px)', value: 'lp-lead' },
        { label: 'Body (20px)', value: 'lp-body' },
        { label: 'Small (16px)', value: 'lp-body-sm' },
        { label: 'Fine print (14px)', value: 'lp-small' },
        { label: 'Eyebrow', value: 'lp-eyebrow' },
      ],
    },
    align: { type: 'radio', label: 'Alignment', options: [...ALIGN_OPTIONS] },
    ...typeStyleFields,
    onDark: { type: 'boolean', label: 'On a dark background' },
  } as BlockSpec<TextBlockProps>['fields'],

  defaultProps: {
    content: '',
    scale: 'lp-body',
    align: 'left',
    ...TYPE_STYLE_DEFAULTS,
    onDark: false,
  },

  toIR(props): IRNode {
    const base = props.scale;
    return el(
      'Paragraph',
      'p',
      textCls(
        base,
        alignCombo(base, props.align),
        ...typeStyleCombos(base, props),
        onDarkCombo(base, props.onDark),
      ),
      parseInline(props.content),
    );
  },

  signals(props): BlockSignal[] {
    return [
      { kind: 'text', fieldPath: 'content', value: plainInline(props.content), scope: 'body' },
      // Inline links count towards the internal-link audit just like a Link block does.
      ...inlineLinks(props.content).map(
        (l): BlockSignal => ({ kind: 'link', href: l.href, anchor: l.label }),
      ),
    ];
  },
};

/* ================================================================ *
 * Button
 * ================================================================ */

export interface ButtonBlockProps extends Record<string, unknown> {
  variant: 'primary' | 'secondary';
  size: ButtonSize;
  radius: ButtonRadius;
  fullWidth: boolean;
  cta: CtaValue;
  align: 'left' | 'center' | 'right';
}

export const ButtonBlock: BlockSpec<ButtonBlockProps> = {
  id: 'ButtonBlock',
  label: 'Button',
  category: 'both',

  fields: {
    variant: {
      type: 'radio',
      label: 'Style',
      help: 'Primary is the tracked yellow button used across the site.',
      options: [
        { label: 'Primary', value: 'primary' },
        { label: 'Secondary', value: 'secondary' },
      ],
    },
    ...buttonStyleFields(),
    cta: { type: 'cta', label: 'Button' },
    align: { type: 'radio', label: 'Alignment', options: [...ALIGN_OPTIONS] },
  } as BlockSpec<ButtonBlockProps>['fields'],

  defaultProps: {
    variant: 'primary',
    ...buttonStyleDefaults(),
    cta: {
      label: 'Collect money now',
      href: 'https://app.collctiv.com/signup',
      analyticsId: 'lpCTA',
      location: 'website',
      utmContent: '',
    },
    align: 'left',
  } as ButtonBlockProps,

  toIR(props): IRNode {
    const wrapper = ctaGroupCls(props.align);
    const child = buttonNode(props.cta, props.variant, buttonStyleFrom(props));
    return el('Block', 'div', wrapper, [child], { displayName: 'Button' });
  },

  signals(props): BlockSignal[] {
    return [{ kind: 'cta', analyticsId: props.cta.analyticsId, tone: props.variant }];
  },

  validate(props): Issue[] {
    const issues: Issue[] = [];
    if (props.variant === 'primary' && !isValidAnalyticsId(props.cta.analyticsId)) {
      issues.push({
        id: 'cta-analytics-id',
        severity: 'error',
        message:
          'A primary button needs a valid analytics position — it becomes the button’s DOM id ' +
          'and the position reported to PostHog.',
        location: { fieldPath: 'cta' },
      });
    }
    if (!props.cta.label.trim()) {
      issues.push({
        id: 'cta-no-label',
        severity: 'error',
        message: 'This button has no label.',
        location: { fieldPath: 'cta' },
      });
    }
    return issues;
  },
};

/* ================================================================ *
 * Image
 * ================================================================ */

export interface ImageBlockProps extends Record<string, unknown> {
  /**
   * The image itself, not a reference to one.
   *
   * Storing an id into a shared asset map meant a duplicated block pointed at the same
   * record, so changing the picture on the copy silently changed the original too. Holding
   * the asset in the block's own props makes duplication a genuine copy.
   */
  image: AssetRef | null;
  width: 'full' | 'half' | 'third' | 'two-thirds';
  radius: string;
  fit: 'cover' | 'contain' | 'fill';
  ratio: string;
  isHero: boolean;
}

export const ImageBlock: BlockSpec<ImageBlockProps> = {
  id: 'ImageBlock',
  label: 'Image',
  category: 'both',

  fields: {
    image: { type: 'image', label: 'Image' },
    width: {
      type: 'select',
      label: 'Width',
      options: [
        { label: 'Full', value: 'full' },
        { label: 'Two thirds', value: 'two-thirds' },
        { label: 'Half', value: 'half' },
        { label: 'One third', value: 'third' },
      ],
    },
    radius: {
      type: 'select',
      label: 'Corner radius',
      options: [
        { label: 'None', value: '' },
        { label: 'Small', value: 'sm' },
        { label: 'Medium', value: 'md' },
        { label: 'Large', value: 'lg' },
      ],
    },
    fit: {
      type: 'select',
      label: 'Fit',
      help: 'How the picture fills its box once a shape is set.',
      options: [
        { label: 'Crop to fill', value: 'cover' },
        { label: 'Fit inside', value: 'contain' },
        { label: 'Stretch', value: 'fill' },
      ],
    },
    ratio: {
      type: 'select',
      label: 'Shape',
      help: 'Set the same shape on several images and they line up, whatever their originals.',
      options: [
        { label: 'Original', value: '' },
        { label: 'Wide 16:9', value: '16-9' },
        { label: 'Photo 3:2', value: '3-2' },
        { label: 'Classic 4:3', value: '4-3' },
        { label: 'Square', value: '1-1' },
        { label: 'Portrait 3:4', value: '3-4' },
      ],
    },
    isHero: {
      type: 'boolean',
      label: 'Above the fold',
      help: 'Loads eagerly with a high fetch priority. Set this on your largest hero image.',
    },
  },

  defaultProps: { image: null, width: 'full', radius: 'md', fit: 'cover', ratio: '', isHero: false },

  toIR(props, ctx: IRContext): IRNode {
    const asset = props.image;
    const hero = props.isHero || ctx.isFirstSection;

    if (!asset?.url) {
      // An empty <img> crashes the Webflow Designer, so render a placeholder box instead.
      return el('Block', 'div', boxCls('is-bg-surface', 'is-radius-md', 'is-minh-sm'), [], {
        displayName: 'Image (empty)',
      });
    }

    const imgCls = textCls(
      'lp-image',
      props.width !== 'full' ? `is-img-${props.width}` : null,
      props.radius !== 'md' ? `is-img-radius-${props.radius || 'none'}` : null,
      props.ratio ? `is-img-ratio-${props.ratio}` : null,
      // The ratio combos already imply cover, so only say it when it differs.
      props.fit !== 'cover' ? `is-img-fit-${props.fit}` : null,
    );

    return el('Image', 'img', imgCls, [], {
      attr: {
        src: asset.url,
        alt: asset.alt ?? '',
        width: String(asset.width),
        height: String(asset.height),
        loading: hero ? 'eager' : 'lazy',
        ...(hero ? { fetchpriority: 'high' } : {}),
      },
      displayName: hero ? 'Hero image' : 'Image',
    });
  },

  signals(props): BlockSignal[] {
    return props.image ? [{ kind: 'image', asset: props.image }] : [];
  },

  validate(props): Issue[] {
    if (!props.image) {
      return [
        {
          id: 'image-empty',
          severity: 'warn',
          message: 'No image chosen — this renders as an empty placeholder.',
          location: { fieldPath: 'image' },
        },
      ];
    }
    return [];
  },
};

/* ================================================================ *
 * Icon
 * ================================================================ */

export interface IconBlockProps extends Record<string, unknown> {
  icon: string;
  chip: boolean;
}

export const IconBlock: BlockSpec<IconBlockProps> = {
  id: 'IconBlock',
  label: 'Icon',
  category: 'both',

  fields: {
    icon: { type: 'icon', label: 'Icon' },
    chip: { type: 'boolean', label: 'Tinted background' },
  },

  defaultProps: { icon: 'gift', chip: true },

  toIR(props): IRNode {
    return el('Block', 'div', props.chip ? ['lp-icon-chip'] : boxCls('is-inline-flex'), [], {
      attr: { 'data-lp-icon': props.icon },
      displayName: 'Icon',
    });
  },
};

/* ================================================================ *
 * List
 * ================================================================ */

export interface ListBlockProps extends Record<string, unknown> {
  items: Array<{ text: string }>;
  ordered: boolean;
  onDark: boolean;
}

export const ListBlock: BlockSpec<ListBlockProps> = {
  id: 'ListBlock',
  label: 'List',
  category: 'both',

  fields: {
    items: {
      type: 'array',
      label: 'Items',
      of: { text: { type: 'text', label: 'Text', lint: 'body' } },
      summaryField: 'text',
    },
    ordered: { type: 'boolean', label: 'Numbered' },
    onDark: { type: 'boolean', label: 'On a dark background' },
  },

  defaultProps: { items: [{ text: 'A benefit worth reading' }], ordered: false, onDark: false },

  toIR(props): IRNode {
    const items = Array.isArray(props.items) ? props.items : [];
    const dark = props.onDark;
    return el(
      'List',
      props.ordered ? 'ol' : 'ul',
      textCls('lp-list', onDarkCombo('lp-list', dark)),
      items.map((item) =>
        el(
          'ListItem',
          'li',
          textCls('lp-list-item', onDarkCombo('lp-list-item', dark)),
          [textNode(item?.text ?? '')],
        ),
      ),
      { displayName: 'List' },
    );
  },

  signals(props): BlockSignal[] {
    const items = Array.isArray(props.items) ? props.items : [];
    return [
      { kind: 'listItems', count: items.length },
      ...items.map((item, i) => ({
        kind: 'text' as const,
        fieldPath: `items[${i}].text`,
        value: item?.text ?? '',
        scope: 'body' as const,
      })),
    ];
  },
};

/* ================================================================ *
 * Divider
 * ================================================================ */

export interface DividerBlockProps extends Record<string, unknown> {
  spacing: string;
}

export const DividerBlock: BlockSpec<DividerBlockProps> = {
  id: 'DividerBlock',
  label: 'Divider',
  category: 'both',

  fields: {
    spacing: {
      type: 'select',
      label: 'Space around',
      options: [
        { label: 'Small', value: '3' },
        { label: 'Medium', value: '5' },
        { label: 'Large', value: '7' },
      ],
    },
  },

  defaultProps: { spacing: '5' },

  toIR(props): IRNode {
    return el('Block', 'div', boxCls(`is-pad-y-${props.spacing}`), [
      el('Block', 'div', ['lp-divider'], []),
    ], { displayName: 'Divider' });
  },
};

/* ================================================================ *
 * Link — an inline text link, for use inside a Stack
 * ================================================================ */

export interface LinkBlockProps extends Record<string, unknown> {
  link: LinkValue;
}

export const LinkBlock: BlockSpec<LinkBlockProps> = {
  id: 'LinkBlock',
  label: 'Text link',
  category: 'both',

  fields: { link: { type: 'link', label: 'Link' } },

  defaultProps: { link: { href: '', label: '' } },

  toIR(props): IRNode {
    const { href, label } = props.link ?? { href: '', label: '' };
    return el('Link', 'a', ['lp-link'], [textNode(label)], { attr: { href } });
  },

  signals(props): BlockSignal[] {
    const { href, label } = props.link ?? { href: '', label: '' };
    return href ? [{ kind: 'link', href, anchor: label }] : [];
  },
};

/* ================================================================ *
 * Embed — the escape hatch
 * ================================================================ */

export interface EmbedBlockProps extends Record<string, unknown> {
  html: string;
}

export const EmbedBlock: BlockSpec<EmbedBlockProps> = {
  id: 'EmbedBlock',
  label: 'Embed / custom code',
  category: 'both',
  description: 'Raw HTML. Use sparingly — nothing here is checked or linted.',

  fields: {
    html: { type: 'textarea', label: 'HTML', help: 'Pasted verbatim into the page.' },
  },

  defaultProps: { html: '' },

  toIR(props): IRNode {
    return { kind: 'embed', html: props.html, cls: [{ name: 'lp-cta-embed' }] };
  },

  validate(props): Issue[] {
    if (!props.html.trim()) {
      return [
        { id: 'embed-empty', severity: 'info', message: 'This embed is empty.', location: { fieldPath: 'html' } },
      ];
    }
    return [];
  },
};

export const contentPrimitives = [
  Heading,
  TextBlock,
  ButtonBlock,
  ImageBlock,
  IconBlock,
  ListBlock,
  LinkBlock,
  DividerBlock,
  EmbedBlock,
];
